<script setup lang="ts">
  defineOptions({ name: 'CPDropdown', inheritAttrs: false })

  import { computed, type PropType } from 'vue'

  import { HLDropdown } from '@platform-ui/highrise'
  import type { HLDropdownOption } from '@platform-ui/highrise'

  import type { CPDropdownOption } from './types/dropdown.types'

  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    /** `manual` leaves opening and closing to the parent through `show`. */
    trigger: {
      type: String as PropType<'click' | 'hover' | 'manual'>,
      required: false,
      default: 'click',
    },
    placement: {
      type: String as PropType<
        | 'top'
        | 'top-start'
        | 'top-end'
        | 'bottom'
        | 'bottom-start'
        | 'bottom-end'
        | 'left'
        | 'left-start'
        | 'left-end'
        | 'right'
        | 'right-start'
        | 'right-end'
      >,
      required: false,
      default: 'bottom',
    },
    offset: {
      type: Number,
      required: false,
      default: 8,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    showSelectedMark: {
      type: Boolean,
      required: false,
      default: false,
    },
    showSearch: {
      type: Boolean,
      required: false,
      default: true,
    },
    options: {
      type: Array as PropType<CPDropdownOption[]>,
      required: false,
      default: () => [],
    },
  })

  const highriseOptions = computed(() => props.options as unknown as HLDropdownOption[])
</script>

<template>
  <HLDropdown
    v-bind="$attrs"
    :id="props.id"
    :trigger="props.trigger"
    :placement="props.placement"
    :offset="props.offset"
    :options="highriseOptions"
    :disabled="props.disabled"
    :show-selected-mark="props.showSelectedMark"
    :show-search="props.showSearch"
  >
    <slot />
  </HLDropdown>
</template>

<!--
  HighRise dropdown overrides — unscoped because HLDropdown teleports
  its menu into document.body. <style scoped> and :deep() cannot reach
  teleported nodes, so the overrides must be globally injected. The
  CSS still ships in this SFC's chunk (Option B from the move-into-CP
  refactor) so ownership lives next to the component that renders the
  surface. Targets:
    .hr-dropdown-menu, .hr-dropdown-menu-wrapper  – outer floating shell
    .hr-dropdown__container.hr-dropdown-menu      – HighRise's inner
                                                    container that owns
                                                    the --n-dropdown-*
                                                    custom-property chain
    .hr-dropdown-option-body                      – per-row surface
                                                    (hard-coded to
                                                    var(--gray-50) upstream)
  HighRise injects --n-* custom properties as inline styles via its JS
  theme engine, so !important is required to beat inline specificity.
-->
<style>
  /* Floating shell — background, text, border for the dropdown popup */
  .hr-dropdown-menu,
  .hr-dropdown-menu-wrapper {
    --gray-50: var(--cp-background-500);
    --gray-100: var(--cp-hover);
    --gray-200: var(--cp-hover);
    --gray-400: var(--cp-font-400);
    --gray-700: var(--cp-brand-font);
    --base-white: var(--cp-background-500);
    --n-color: var(--cp-background-500) !important;
    --n-menu-color: var(--cp-background-500) !important;
    --n-dropdown-color: var(--cp-background-500) !important;
    --n-text-color: var(--cp-brand-font) !important;
    --n-border: 1px solid var(--cp-border-500) !important;
    background-color: var(--cp-background-500) !important;
    border: 1px solid var(--cp-border-500) !important;
  }

  /*
   * Dropdown container and option rows.
   *
   * HighRise's actual rendered structure is:
   *   .hr-dropdown__container.hr-dropdown-menu  →  uses --n-dropdown-color
   *   .hr-dropdown-option-body                  →  hard-codes background-color: var(--gray-50)
   *
   * --n-color (set above) is NOT the same variable, so we must override
   * --n-dropdown-color and directly patch the option-body background.
   */
  .hr-dropdown__container.hr-dropdown-menu {
    --n-dropdown-color: var(--cp-background-500) !important;
    --n-dropdown-option-text-color: var(--cp-brand-font) !important;
    --n-dropdown-option-text-color-active: var(--cp-brand-font) !important;
    /* Both hover and active/selected use the same themed overlay */
    --n-option-color-hover: var(--cp-hover) !important;
    --n-dropdown-option-color-hover: var(--cp-hover) !important;
    --n-option-color-active: var(--cp-hover) !important;
    --n-dropdown-option-color-active: var(--cp-hover) !important;
    border: 1px solid var(--cp-border-500) !important;
  }

  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option-body {
    background-color: var(--cp-background-500) !important;
    color: var(--cp-brand-font) !important;
  }

  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option-body::before {
    background-color: transparent !important;
  }

  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option-body:not(.hr-dropdown-option-body--disabled):not(.search):hover {
    background-color: var(--cp-hover) !important;
  }

  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option--interactive:hover,
  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown__menu-item--interactive:hover,
  .hr-dropdown-menu .hr-dropdown-option--interactive:hover,
  .hr-dropdown-menu .hr-dropdown__menu-item--interactive:hover {
    background-color: var(--cp-hover) !important;
  }

  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option-body:not(.hr-dropdown-option-body--disabled):not(.search):hover::before,
  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option-body.hr-dropdown-option-body--active::before,
  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option--selected .hr-dropdown-option-body::before {
    background-color: var(--cp-hover) !important;
  }

  /* Selected/active option — use the same hover overlay so it matches
     the popover pattern exactly (subtle tint, brand font text) */
  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option-body.hr-dropdown-option-body--active,
  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option--selected .hr-dropdown-option-body {
    background-color: var(--cp-hover) !important;
    color: var(--cp-brand-font) !important;
  }

  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option-body--disabled {
    background-color: var(--cp-background-500) !important;
    color: color-mix(in oklab, var(--cp-brand-font) 40%, transparent) !important;
  }

  .hr-dropdown-menu .hr-dropdown-option-body.search,
  .hr-dropdown__container.hr-dropdown-menu .hr-dropdown-option-body.search,
  .hr-dropdown-menu .hr-base-select-menu__input,
  .hr-dropdown-menu .n-base-select-menu__input {
    background-color: var(--cp-background-500) !important;
    color: var(--cp-brand-font) !important;
  }

  .hr-dropdown-menu .hr-dropdown-option-body.search .hr-input,
  .hr-dropdown-menu .hr-dropdown-option-body.search .n-input,
  .hr-dropdown-menu .hr-dropdown-option-body.search .hr-input-wrapper {
    background-color: var(--cp-background-500) !important;
  }
</style>
